import React, { FC, useEffect, useRef, useState } from "react";
import { Badge, Card, Collapse, FormControl } from "react-bootstrap";
import { MutatorProperty } from "../../../models/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import styles from "./CustomTypeCard.module.scss";
import cx from "classnames";

interface Props {
    fieldProperty: MutatorProperty;
    typeProperty: MutatorProperty;
    showMetaGlobal: boolean;
    getVariantByType: (type: string) => string;
    onChange: (name: string, value: number) => void;
}

const MutatorNumberFieldCardImplementation: FC<Props> = ({
    fieldProperty,
    typeProperty,
    showMetaGlobal,
    getVariantByType,
    onChange
}) => {
    const inputRef = useRef(null);
    const [value, updateValue] = useState(fieldProperty.value);
    const [showMeta, updateShowMeta] = useState(showMetaGlobal);
    const [notification, updateNotification] = useState<string | undefined>(
        undefined
    );

    useEffect(() => {
        updateShowMeta(showMetaGlobal);
    }, [showMetaGlobal]);

    useEffect(() => {
        if (fieldProperty.value && fieldProperty.value !== value) {
            updateValue(fieldProperty.value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldProperty.value]);

    const forceClickBehaviour = () => {
        // Some browers do not focus the form properly when clicking increase / decrease
        if (inputRef.current) {
            // @ts-ignore
            inputRef.current.focus();
        }
    };

    return (
        <Card className="text-inverse inner-card-other">
            <Card.Header
                className={`d-flex align-items-center ${styles.cardHeader}`}
            >
                {fieldProperty.name}
                <Badge
                    className="mx-2"
                    bg={getVariantByType(fieldProperty.type)}
                >
                    {fieldProperty.type}
                </Badge>
                {typeProperty.meta ? (
                    <div
                        className="float-end ms-auto mouseHover"
                        onClick={() => updateShowMeta(!showMeta)}
                        title="Help and additional info"
                    >
                        <FontAwesomeIcon
                            icon={faCircleInfo}
                            className={cx("ms-2", {
                                "text-inverse-25": !showMeta,
                                "text-inverse-50": showMeta
                            })}
                        />
                    </div>
                ) : null}
            </Card.Header>
            <Card.Body className={`${styles.cardBody}`}>
                <FormControl
                    ref={inputRef}
                    className={styles.inputGroup}
                    type="number"
                    placeholder="Add value"
                    value={value}
                    onClick={forceClickBehaviour}
                    onChange={e => {
                        const newValueAsNumber = Number(e.target.value);
                        const trimmedInput = e.target.value.trim();
                        if (
                            // @ts-ignore
                            !(
                                !isNaN(parseFloat(trimmedInput)) &&
                                // @ts-ignore
                                isFinite(trimmedInput)
                            )
                        ) {
                            updateNotification("Valid number is required");
                        } else if (
                            typeProperty.meta?.min &&
                            newValueAsNumber < typeProperty.meta.min
                        ) {
                            updateNotification(
                                `Minimum allowed value ${typeProperty.meta.min}`
                            );
                        } else if (
                            typeProperty.meta?.max &&
                            newValueAsNumber > typeProperty.meta.max
                        ) {
                            updateNotification(
                                `Maximum allowed value ${typeProperty.meta.max}`
                            );
                        } else {
                            updateNotification(undefined);
                        }
                        updateValue(e.target.value);
                    }}
                    onBlur={e => {
                        const newValue = Number(e.target.value);
                        const trimmedInput = e.target.value.trim();
                        if (
                            // @ts-ignore
                            !(
                                !isNaN(parseFloat(trimmedInput)) &&
                                // @ts-ignore
                                isFinite(trimmedInput)
                            )
                        ) {
                            updateNotification("Valid number is required");
                            return;
                        } else if (
                            typeProperty.meta?.min &&
                            newValue < typeProperty.meta.min
                        ) {
                            updateNotification(
                                `Minimum allowed value ${typeProperty.meta.min}`
                            );
                            return;
                        } else if (
                            typeProperty.meta?.max &&
                            newValue > typeProperty.meta.max
                        ) {
                            updateNotification(
                                `Maximum allowed value ${typeProperty.meta.max}`
                            );
                            return;
                        } else {
                            updateNotification(undefined);
                        }
                        onChange(typeProperty.name, newValue);
                    }}
                />
            </Card.Body>
            {typeProperty.meta ? (
                <Collapse in={showMeta}>
                    <div>
                        <Card.Footer className={styles.footerInfo}>
                            {typeProperty.meta.description ? (
                                <div className="bread-text text-inverse-50">
                                    {typeProperty.meta.description}
                                </div>
                            ) : (
                                <span className="bread-text text-inverse-50">
                                    No description provided.
                                </span>
                            )}
                            {typeProperty.meta.min ? (
                                <div className="bread-text text-info">
                                    <span>Min: {typeProperty.meta.min}</span>
                                </div>
                            ) : null}
                            {typeProperty.meta.max ? (
                                <div className="bread-text text-info">
                                    <span>Max: {typeProperty.meta.max}</span>
                                </div>
                            ) : null}
                        </Card.Footer>
                    </div>
                </Collapse>
            ) : null}
            {notification ? (
                <Card.Footer className={styles.footerInfo}>
                    <div className="bread-text text-danger">{notification}</div>
                </Card.Footer>
            ) : null}
        </Card>
    );
};

export const MutatorNumberFieldCard = React.memo(
    MutatorNumberFieldCardImplementation
);
